import { globalRef } from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { globalData } from '@amzn/bf-playback'

/**
 * App-wide multi-select state for track rows.
 *
 * A single module-level observable holds the currently-selected track
 * asins so any track row (whichever page it lives on) can toggle its
 * own membership, and the persistent `BulkActionsToolbar` mounted at
 * app-root can render the selection count + bulk actions without
 * threading state through each page.
 *
 * Green hornet uses a similar app-wide multi-select bar (`#multiSelectBar`)
 * that overlays the bottom nav when a track is selected; we mirror that
 * shape but keep the store simple (a `Set<string>` of asins).
 */

const state$ = globalData(() => data<ReadonlySet<string>>(new Set()))

/**
 * Snapshot of currently-selected track asins. Subscribe from any surface
 * that needs to react to the selection (row checkboxes, the toolbar).
 */
export const selectedTracks$: ObservableValue<ReadonlySet<string>> = state$

/**
 * Derived: true when at least one track is selected. Row renderers read
 * this to decide whether to enter "multi-select mode" (surface checkbox
 * column, swap row click to toggle instead of play).
 */
export const isMultiSelectMode$: ObservableValue<boolean> = state$.map((s) => s.size > 0)

const used$ = globalData(() => data(false))

/** True from the first selection on, so the bulk-actions toolbar loads only once someone selects. */
export const trackSelectionUsed$: ObservableValue<boolean> = used$

function setSelection(next: ReadonlySet<string>): void {
    if (next.size > 0) used$.value = true
    state$.value = next
}

/**
 * Remembers the last track the user selected so shift-click can select
 * a contiguous range. Set by `toggleSelection` on every add, and read /
 * cleared by `selectRange`. Not exported — the renderer only cares
 * about the store's public API.
 */
const shiftAnchorRef = globalRef<{ id: string | null }>(() => ({ id: null }))

/**
 * Toggle a single track's membership. If the track was already
 * selected it's removed; otherwise it's added. Updates the
 * shift-anchor to this id on any add so a subsequent shift-click
 * can extend from here.
 */
export function toggleSelection(trackId: string): void {
    const next = new Set(state$.value)
    if (next.has(trackId)) {
        next.delete(trackId)
    } else {
        next.add(trackId)
        shiftAnchorRef.value.id = trackId
    }
    setSelection(next)
}

/**
 * Select every id in `ids` between (and including) the shift-anchor
 * and `toId`. Falls back to a single toggle when there's no anchor
 * yet — the first shift-click behaves like a plain click. The caller
 * is expected to pass the row order it renders in (typically the
 * flat array of visible track ids); the store slices from that.
 */
export function selectRange(ids: readonly string[], toId: string): void {
    const fromId = shiftAnchorRef.value.id
    if (fromId == null) {
        toggleSelection(toId)
        return
    }
    const fromIndex = ids.indexOf(fromId)
    const toIndex = ids.indexOf(toId)
    if (fromIndex < 0 || toIndex < 0) {
        // Shift-anchor is no longer visible (row scrolled out of a
        // paged list) — fall back to a plain toggle.
        toggleSelection(toId)
        return
    }
    const [lo, hi] = fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex]
    const next = new Set(state$.value)
    for (let i = lo; i <= hi; i++) {
        next.add(ids[i])
    }
    shiftAnchorRef.value.id = toId
    setSelection(next)
}

/** Empty the selection. Called by the toolbar's close (×) affordance. */
export function clearSelection(): void {
    if (state$.value.size === 0) return
    shiftAnchorRef.value.id = null
    state$.value = new Set()
}
